import Alert from '@xo-union/tk-component-alerts';
import Spinner from '@xo-union/tk-component-spinner';
import Honeybadger from 'honeybadger-js';
import React, { ChangeEvent, useRef, useState } from 'react';

import { GifItemsFragment, useCreateGifItemMutation } from '../../../generated/graphql';
import addItemToPage from '../../../helpers/apolloCache/addItemToPage';
import dataURIToBlob from '../../../helpers/dataURIToBlob';
import { loadImageWithoutValidation, useImageUploader } from '../../../helpers/images/useImageUploader';
import { useManageItem } from '../../../helpers/useManageItem';
import { pagesQuery } from '../../LeftContainer/graphql';
import { PageItemIcon } from '../PageItemIcon';
import styles from '../pageItemSelector.scss';

interface GifItemFieldProps {
  pageId: number;
}

const GifItemField = ({ pageId }: GifItemFieldProps) => {
  const { imageUpload } = useImageUploader();
  const alertRef = useRef<any>(null);
  const inputFileRef = useRef<HTMLInputElement>(null);
  const { close } = useManageItem();
  const [createGif] = useCreateGifItemMutation();
  const [loading, setLoading] = useState(false);

  const showError = (message: string) => {
    alertRef.current.showAndDismiss({
      type: 'error',
      content: message,
      waitTime: 3000,
    });
  };

  const onChange = async (event: ChangeEvent<HTMLInputElement>) => {
    setLoading(true);
    const { files } = event.target;
    if (!files || !files[0]) return;
    const image = await loadImageWithoutValidation(files[0]);

    await saveImage({ file: dataURIToBlob(image?.img?.src) });
  };

  const saveImage = async ({ file }: { file: File }) => {
    try {
      const image = await imageUpload({ file });

      if (!image || image.error) {
        setLoading(false);
      }

      await createGif({
        variables: {
          attributes: {
            pageId,
            mediaApiId: image?.data?.mediaApiId || '',
          },
        },
        update: (cache, { data }) => {
          setLoading(false);
          addItemToPage<GifItemsFragment | undefined>(cache, pageId, data?.createGifItem, close);
        },
        refetchQueries: [{ query: pagesQuery }],
      });
    } catch (error) {
      showError('Error uploading Gif');
      Honeybadger.notify(error, {
        message: 'Error uploading GifItem',
        context: {
          error,
        },
      });
    }
  };

  const onKeyDown = React.useCallback((e: React.KeyboardEvent<HTMLLabelElement>) => {
    if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault();
      inputFileRef?.current?.click();
    }
  }, []);

  return (
    <>
      <label
        className={styles.clickable}
        htmlFor="gif-item-input"
        tabIndex={0}
        onKeyDown={onKeyDown}
        role="button"
        aria-label="Gif"
      >
        <input
          ref={inputFileRef}
          id="gif-item-input"
          type="file"
          accept="image/gif"
          multiple={false}
          onChange={onChange}
          style={{ display: 'none' }}
          data-testid="gif-item-upload-input"
        />
        {loading ? (
          <div className={styles.loading}>
            <Spinner data-testid="loading-spinner" />
          </div>
        ) : (
          <PageItemIcon icon="GifItem" />
        )}
      </label>
      <Alert ref={alertRef} position="bottom" />
    </>
  );
};

export default GifItemField;
